import 'react-quill/dist/quill.snow.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactQuill, { Quill } from 'react-quill';
import { makeStyles } from '@material-ui/core/styles';
import axios from '../../../axios';
import imageBasePath from '../../../config';
import { convertImageToBase64 } from '../../util/convertImageToBase64';

const IMAGE_BASE = String(imageBasePath || '').replace(/\/+$/, '');

// Allow blob: URLs for instant image previews (Quill otherwise sanitizes to "//:0")
// and then we replace blob: with the uploaded image URL in background.
try {
  if (typeof window !== 'undefined' && Quill?.import) {
    const Image = Quill.import('formats/image');
    if (Image && !Image.__allowBlobSanitizePatched) {
      const originalSanitize = Image.sanitize;
      // eslint-disable-next-line no-param-reassign
      Image.sanitize = (url) => {
        const u = String(url || '');
        if (u.startsWith('blob:')) return u;
        return typeof originalSanitize === 'function' ? originalSanitize(u) : u;
      };
      // eslint-disable-next-line no-param-reassign
      Image.__allowBlobSanitizePatched = true;
    }
  }
} catch {
  // ignore
}

/* -------------------- styles -------------------- */
const useStyles = makeStyles(() => ({
  editor: {
    '& .ql-container': {
      minHeight: 250,
      fontSize: 16,
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8,
    },
    '& .ql-toolbar': {
      background: '#fff',
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
    },
  },
}));

/* -------------------- safe html cleaner -------------------- */
const cleanHtml = (html = '') => {
  if (!html || typeof html !== 'string') return '<p><br></p>';

  let clean = html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/javascript:/gi, '');

  if (!/^<(p|div|br|img)/i.test(clean.trim())) {
    clean = `<p>${clean}</p>`;
  }

  clean = clean.replace(/<p>\s*<\/p>/g, '<p><br></p>');
  return clean;
};

/* -------------------- component -------------------- */
const RichTextEditor = ({ content, placeholder, handleContentChange }) => {
  const classes = useStyles();
  const quillRef = useRef(null);
  const [ready, setReady] = useState(false);
  const isReplacingBase64Ref = useRef(false);
  const uploadedDataUrlCacheRef = useRef(new Map());
  const lastEmittedStorageHtmlRef = useRef('');

  const isExternalImageSrc = useCallback((src) => {
    const s = String(src || '');
    if (!s) return false;

    // Allow our own absolute URLs under base
    if (IMAGE_BASE && s.startsWith(`${IMAGE_BASE}/`)) return false;

    // Disallow external image links (keep them out of stored HTML and don't upload)
    return /^(https?:\/\/|\/\/)/i.test(s);
  }, []);

  const toDisplaySrc = useCallback((src) => {
    const s = String(src || '');
    if (!s) return s;
    if (/^(https?:|data:|blob:|\/\/)/i.test(s)) return s;
    const normalized = s.replace(/^\/+/, '');
    return `${IMAGE_BASE}/${normalized}`;
  }, []);

  const toDisplayHtml = useCallback(
    (html) => {
      const input = String(html || '');
      if (!input) return input;

      try {
        // Prefer a fast regex transform; DOMParser can behave differently across browsers
        // and we only need to rewrite <img src="..."> here.
        return input.replace(
          /(<img\b[^>]*\bsrc\s*=\s*)(['"]?)(?!https?:|data:|blob:|\/\/)([^'"\s>]+)\2/gi,
          (m, pre, q, src) => `${pre}${q}${toDisplaySrc(src)}${q}`
        );
      } catch {
        return input;
      }
    },
    [toDisplaySrc]
  );

  const toStorageHtml = useCallback((html) => {
    const input = String(html || '');
    if (!input) return input;

    try {
      if (typeof window === 'undefined' || !window.DOMParser) return input;
      const doc = new window.DOMParser().parseFromString(input, 'text/html');

      doc.querySelectorAll('img').forEach((img) => {
        const src = img.getAttribute('src') || '';
        if (!src) return;

        // Never persist local preview URLs
        if (src.startsWith('blob:')) {
          img.remove();
          return;
        }

        // Never persist external image links
        if (isExternalImageSrc(src)) {
          img.remove();
          return;
        }

        // Strip base path so DB stores only relative file path
        if (IMAGE_BASE && src.startsWith(`${IMAGE_BASE}/`)) {
          img.setAttribute('src', src.slice(IMAGE_BASE.length + 1));
          return;
        }

        // Also handle accidental double slashes
        if (IMAGE_BASE && src.startsWith(`${IMAGE_BASE}//`)) {
          img.setAttribute('src', src.slice(IMAGE_BASE.length + 2));
        }
      });

      return doc.body.innerHTML;
    } catch {
      // Fallback: strip external linked images via regex
      return input.replace(
        /<img\b[^>]*\bsrc\s*=\s*(['"]?)(https?:\/\/|\/\/)[^'"\s>]+\1[^>]*>/gi,
        ''
      );
    }
  }, [isExternalImageSrc]);

  const stripExternalImagesFromHtml = useCallback(
    (html) => {
      const input = String(html || '');
      if (!input) return input;

      try {
        if (typeof window === 'undefined' || !window.DOMParser) {
          return input.replace(
            /<img\b[^>]*\bsrc\s*=\s*(['"]?)(https?:\/\/|\/\/)[^'"\s>]+\1[^>]*>/gi,
            ''
          );
        }

        const doc = new window.DOMParser().parseFromString(input, 'text/html');
        doc.querySelectorAll('img').forEach((img) => {
          const src = img.getAttribute('src') || '';
          if (!src) return;
          if (isExternalImageSrc(src)) img.remove();
        });
        return doc.body.innerHTML;
      } catch {
        return input.replace(
          /<img\b[^>]*\bsrc\s*=\s*(['"]?)(https?:\/\/|\/\/)[^'"\s>]+\1[^>]*>/gi,
          ''
        );
      }
    },
    [isExternalImageSrc]
  );

  /* SSR safe mount */
  useEffect(() => {
    setReady(true);
  }, []);

  /* inject external html safely */
  useEffect(() => {
    if (!ready || !quillRef.current) return;

    const editor = quillRef.current.getEditor();
    const cleanedStorage = cleanHtml(content);

    // If parent is echoing back what we emitted, don't re-inject (cursor stays stable)
    if (lastEmittedStorageHtmlRef.current && cleanedStorage === lastEmittedStorageHtmlRef.current) {
      return;
    }

    const cleanedDisplay = toDisplayHtml(cleanedStorage);
    if (editor.root.innerHTML === cleanedDisplay) return;

    try {
      const hadFocus = typeof editor.hasFocus === 'function' ? editor.hasFocus() : false;
      const sel = editor.getSelection();

      editor.setContents(editor.clipboard.convert(cleanedDisplay), 'silent');

      if (hadFocus && sel) {
        const max = Math.max(0, editor.getLength() - 1);
        editor.setSelection(Math.min(sel.index, max), sel.length || 0, 'silent');
        editor.focus();
      }
    } catch {
      editor.setText('');
    }
  }, [content, ready, toDisplayHtml]);

  /* -------------------- image upload -------------------- */
  const uploadBase64Image = useCallback(async (dataUrl) => {
    // Expecting full dataURL: "data:image/...;base64,...."
    // If caller passes only raw base64, wrap it as dataURL (jpeg fallback).

    const dataUrlStr = String(dataUrl || '');
    const isDataUrl = /^data:image\/[a-z0-9.+-]+;base64,/i.test(dataUrlStr);
    const image = isDataUrl ? dataUrlStr : `data:image/jpeg;base64,${dataUrlStr}`;

    // Send JSON instead of multipart/form-data (no FormData)
    // Assumption: backend accepts { image: <dataUrl> } and returns { path }
    const res = await axios.post(
      '/image-upload',
      { image },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const rawPath = res?.data?.data ?? '';

    if (!rawPath) throw new Error('Invalid image upload response');

    const raw = String(rawPath);
    // If backend returns absolute URL and it's under IMAGE_BASE, strip to relative.
    if (/^https?:\/\//i.test(raw)) {
      if (IMAGE_BASE && raw.startsWith(`${IMAGE_BASE}/`)) return raw.slice(IMAGE_BASE.length + 1);
      return raw; // fallback (can't safely convert)
    }

    // Prefer returning relative path for storage: "public/richtext/....jpg"
    return raw.replace(/^\/+/, '');
  }, []);

  const findImageEmbedIndexBySrc = (editor, src) => {
    try {
      const delta = editor.getContents();
      let idx = 0;
      const ops = delta?.ops || [];

      for (let i = 0; i < ops.length; i += 1) {
        const op = ops[i];
        if (typeof op.insert === 'string') {
          idx += op.insert.length;
          continue;
        }

        if (op.insert && typeof op.insert === 'object' && op.insert.image) {
          if (op.insert.image === src) return idx;
          idx += 1;
          continue;
        }

        // Any other embed
        idx += 1;
      }
    } catch {
      // ignore
    }

    return -1;
  };

  const clampInsertIndex = (editor, idx) => {
    const max = Math.max(0, (editor?.getLength?.() ?? 1) - 1);
    const n = Number.isFinite(idx) ? idx : max;
    return Math.min(Math.max(0, n), max);
  };

  const insertImageFromFile = useCallback(async (file, insertIndex) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor || !file) return;

    const selectionIndex = editor.getSelection?.()?.index;
    const preferredIndex =
      typeof insertIndex === 'number'
        ? insertIndex
        : typeof selectionIndex === 'number'
          ? selectionIndex
          : (editor.getLength?.() ?? 1) - 1;

    const index = clampInsertIndex(editor, preferredIndex);

    // Show immediate preview (blob URL) while uploading
    const objectUrl = URL.createObjectURL(file);
    editor.insertEmbed(index, 'image', objectUrl);
    editor.setSelection(clampInsertIndex(editor, index + 1));

    try {
      const dataUrl = await convertImageToBase64(file);
      const uploadedPathOrUrl = await uploadBase64Image(dataUrl);
      const imageUrl = toDisplaySrc(uploadedPathOrUrl);

      const embedIndex = findImageEmbedIndexBySrc(editor, objectUrl);
      if (embedIndex >= 0) {
        const sel = editor.getSelection();
        editor.deleteText(embedIndex, 1);
        editor.insertEmbed(embedIndex, 'image', imageUrl);

        if (sel) {
          editor.setSelection(sel.index, sel.length);
        }
      }

      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      alert('Image upload failed');
      URL.revokeObjectURL(objectUrl);
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }, [uploadBase64Image, toDisplaySrc]);

  /* -------------------- custom image handler -------------------- */
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const editor = quillRef.current.getEditor();
      const range = editor.getSelection(true);
      await insertImageFromFile(file, range?.index);
    };
  }, [insertImageFromFile]);

  const extractBase64ImageDataUrlsFromHtml = (html) => {
    if (!html || typeof html !== 'string') return [];
    const matches = html.match(/data:image\/[a-z0-9.+-]+;base64,[a-z0-9+/=]+/gi) || [];
    // de-dupe while preserving order
    return Array.from(new Set(matches));
  };

  const replaceBase64ImagesWithUploadedUrls = async (html) => {
    if (!quillRef.current) return;
    if (!html || typeof html !== 'string') return;
    if (isReplacingBase64Ref.current) return;
    if (!html.includes('data:image/')) return;

    const dataUrls = extractBase64ImageDataUrlsFromHtml(html);
    if (dataUrls.length === 0) return;

    isReplacingBase64Ref.current = true;
    try {
      let nextHtml = html;
      for (let idx = 0; idx < dataUrls.length; idx += 1) {
        const dataUrl = dataUrls[idx];

        const cached = uploadedDataUrlCacheRef.current.get(dataUrl);
        if (cached) {
          nextHtml = nextHtml.split(dataUrl).join(cached);
          continue;
        }

        // Upload base64 → URL replace
        const uploadedPathOrUrl = await uploadBase64Image(dataUrl);
        const displayUrl = toDisplaySrc(uploadedPathOrUrl);
        uploadedDataUrlCacheRef.current.set(dataUrl, displayUrl);
        nextHtml = nextHtml.split(dataUrl).join(displayUrl);
      }

      if (nextHtml !== html) {
        const editor = quillRef.current.getEditor();
        const sel = editor.getSelection();
        editor.clipboard.dangerouslyPasteHTML(nextHtml);
        if (sel) editor.setSelection(sel);
      }
    } catch (err) {
      // If conversion fails, keep content as-is; user can still submit.
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      isReplacingBase64Ref.current = false;
    }
  };

  /* -------------------- paste / drop image upload -------------------- */
  useEffect(() => {
    if (!ready || !quillRef.current) return;

    const editor = quillRef.current.getEditor();
    const root = editor?.root;
    if (!root) return;

    const onPaste = (e) => {
      const items = e.clipboardData?.items ? Array.from(e.clipboardData.items) : [];
      const imageItems = items.filter((it) => it.kind === 'file' && it.type?.startsWith('image/'));
      if (imageItems.length === 0) {
        // If user pastes HTML that contains external linked images, strip them.
        const html = e.clipboardData?.getData?.('text/html') || '';
        if (!html || !/(<img\b)/i.test(html)) return;

        const cleaned = stripExternalImagesFromHtml(html);
        if (cleaned === html) return;

        e.preventDefault();
        e.stopPropagation();

        window.setTimeout(() => {
          const selIndex =
            editor.getSelection?.()?.index ?? Math.max(0, (editor.getLength?.() ?? 1) - 1);
          editor.clipboard.dangerouslyPasteHTML(clampInsertIndex(editor, selIndex), cleaned);
        }, 0);
        return;
      }

      // Directly show preview; upload in background; then replace with URL
      e.preventDefault();
      e.stopPropagation();

      const files = imageItems.map((it) => it.getAsFile()).filter(Boolean);
      // Defer insert until after paste event finishes (prevents Quill DOM errors)
      window.setTimeout(() => {
        const base =
          editor.getSelection?.()?.index ?? Math.max(0, (editor.getLength?.() ?? 1) - 1);
        const baseIndex = clampInsertIndex(editor, base);
        files.forEach((file, i) => {
          void insertImageFromFile(file, baseIndex + i);
        });
      }, 0);
    };

    const onDrop = (e) => {
      const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
      const imageFile = files.find((f) => f?.type?.startsWith('image/'));
      if (!imageFile) return;

      e.preventDefault();
      e.stopPropagation();
      // Drop should focus, but defer insert one tick to avoid DOM range issues
      try {
        editor.focus();
      } catch {
        // ignore
      }
      window.setTimeout(() => {
        const index = editor.getSelection?.()?.index;
        void insertImageFromFile(imageFile, index);
      }, 0);
    };

    const onDragOver = (e) => {
      if (e.dataTransfer?.types?.includes?.('Files')) {
        e.preventDefault();
      }
    };

    // Capture phase so we run before Quill's own handlers
    root.addEventListener('paste', onPaste, true);
    root.addEventListener('drop', onDrop, true);
    root.addEventListener('dragover', onDragOver, true);

    return () => {
      root.removeEventListener('paste', onPaste, true);
      root.removeEventListener('drop', onDrop, true);
      root.removeEventListener('dragover', onDragOver, true);
    };
  }, [ready, insertImageFromFile, stripExternalImagesFromHtml]);

  /* -------------------- quill config (memoized to prevent re-init) -------------------- */
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ align: [] }],
          [{ color: [] }, { background: [] }],
          ['link', 'image', 'video'],
          ['clean'],
        ],
        handlers: {
          image: imageHandler, // 🚀 no base64
        },
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    [imageHandler]
  );

  const formats = useMemo(
    () => [
      'header',
      'bold',
      'italic',
      'underline',
      'strike',
      'list',
      'bullet',
      'indent',
      'align',
      'color',
      'background',
      'link',
      'image',
      'video',
    ],
    []
  );

  if (!ready) {
    return <div style={{ minHeight: 250, background: '#fff', borderRadius: 8 }} />;
  }

  return (
    <ReactQuill
      ref={quillRef}
      theme="snow"
      className={classes.editor}
      placeholder={placeholder || 'Write here...'}
      modules={modules}
      formats={formats}
      onChange={(html) => {
        const storageHtml = cleanHtml(toStorageHtml(html));
        lastEmittedStorageHtmlRef.current = storageHtml;
        handleContentChange(storageHtml);
        void replaceBase64ImagesWithUploadedUrls(html);
      }}
    />
  );
};

RichTextEditor.propTypes = {
  content: PropTypes.string,
  placeholder: PropTypes.string,
  handleContentChange: PropTypes.func.isRequired,
};

RichTextEditor.defaultProps = {
  content: '',
  placeholder: '',
};

export default RichTextEditor;
